import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Lock, Loader2, Save, KeyRound } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = { admin: "Super User", manager: "Manager", team_lead: "Team Lead", agent: "Agent" };
const ROLE_COLOR = { admin: "#C23A00", manager: "#8A6D00", team_lead: "#1D4ED8", agent: "#0E6B3F" };

// Friendly labels for module-level permissions
const PERM_LABEL = {
  "support:view": "Support · View",
  "support:manage": "Support · Manage",
  "customer_success:view": "Customer Success · View",
  "customer_success:manage": "Customer Success · Manage",
  "sql:execute": "SQL Scripts · Execute",
  "admin:access": "Administration · Access",
  "reports:view": "Reports · View",
  "analytics:view": "Analytics · View",
  "infrastructure:manage": "Infrastructure · Manage",
};

export default function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});   // role -> Set(permissions)
  const [savingRole, setSavingRole] = useState(null);

  const canEdit = (() => {
    try { return (JSON.parse(localStorage.getItem("equipr_session") || "{}").role) === "admin"; }
    catch { return false; }
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/roles`);
      setRoles(res.data.roles || []);
      setAllPerms(res.data.all_permissions || []);
      const d = {};
      (res.data.roles || []).forEach(r => {
        if (!r.is_super) d[r.role] = new Set(r.permissions);
      });
      setDraft(d);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "You don't have access to Administration" : "Failed to load roles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (role, perm) => {
    if (!canEdit) return;
    setDraft(prev => {
      const next = { ...prev, [role]: new Set(prev[role]) };
      if (next[role].has(perm)) next[role].delete(perm);
      else next[role].add(perm);
      return next;
    });
  };

  const isDirty = (role) => {
    const orig = roles.find(r => r.role === role);
    if (!orig || orig.is_super) return false;
    const a = new Set(orig.permissions), b = draft[role] || new Set();
    if (a.size !== b.size) return true;
    for (const p of a) if (!b.has(p)) return true;
    return false;
  };

  const saveRole = async (role) => {
    setSavingRole(role);
    try {
      const perms = Array.from(draft[role] || []);
      await axios.put(`${API}/admin/roles/${role}`, { permissions: perms });
      setRoles(prev => prev.map(r => r.role === role ? { ...r, permissions: perms } : r));
      toast.success(`${ROLE_LABEL[role]} permissions saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSavingRole(null);
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-[#8A857A]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  const perms = allPerms.length ? allPerms : Object.keys(PERM_LABEL);

  return (
    <div className="space-y-6" data-testid="admin-roles-view">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center">
          <KeyRound className="w-5 h-5 text-[#1D4ED8]" />
        </div>
        <div>
          <h2 className="heading text-xl text-[#1A1A1A] tracking-tight">Roles &amp; Permissions</h2>
          <p className="text-sm text-[#59554D]">
            {canEdit ? "Toggle module permissions per role — changes apply immediately" : "Read-only — only a Super User can edit permissions"}
          </p>
        </div>
      </div>

      {!canEdit && (
        <div className="flex items-center gap-2 text-sm text-[#6B5600] bg-[#FFF7D1] border border-[#FFF7D1] rounded-lg px-4 py-2">
          <Lock className="w-4 h-4" /> You can view the permission matrix but only a Super User (admin) can change it.
        </div>
      )}

      <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#DAD5CA]">
                <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wider text-[#8A857A] font-semibold sticky left-0 bg-[#FFFFFF]">Permission</th>
                {roles.map(r => (
                  <th key={r.role} className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ background: `${ROLE_COLOR[r.role]}1A`, color: ROLE_COLOR[r.role] }}>
                      {r.is_super && <ShieldCheck className="w-3 h-3" />} {ROLE_LABEL[r.role]}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {perms.map(perm => (
                <tr key={perm} className="border-b border-[#F4F2ED] hover:bg-[#FFFFFF]">
                  <td className="px-5 py-3 text-[#1A1A1A] sticky left-0 bg-[#FFFFFF]">{PERM_LABEL[perm] || perm}</td>
                  {roles.map(r => (
                    <td key={r.role} className="px-4 py-3 text-center">
                      {r.is_super ? (
                        <ShieldCheck className="w-4 h-4 text-[#C23A00] mx-auto" title="Super user has all permissions" />
                      ) : (
                        <Checkbox
                          checked={draft[r.role]?.has(perm) || false}
                          onCheckedChange={() => toggle(r.role, perm)}
                          disabled={!canEdit}
                          data-testid={`perm-${r.role}-${perm.replace(/[^a-z]/gi, "-")}`}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Save row */}
              {canEdit && (
                <tr>
                  <td className="px-5 py-3 sticky left-0 bg-[#FFFFFF]" />
                  {roles.map(r => (
                    <td key={r.role} className="px-4 py-3 text-center">
                      {r.is_super ? (
                        <span className="text-xs text-[#8A857A]">locked</span>
                      ) : (
                        <Button size="sm" onClick={() => saveRole(r.role)} disabled={!isDirty(r.role) || savingRole === r.role}
                          className="bg-[#0E6B3F] hover:bg-[#E3F3E9] text-black h-8 disabled:opacity-40"
                          data-testid={`save-role-${r.role}`}>
                          {savingRole === r.role ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        </Button>
                      )}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
